// 設定：音量・演出速度・揺れ軽減・通知・ブロック一覧・Google連携・ログアウト・アカウント削除・規約類。
import { resetTutorial } from '../tutorial';
import { api } from '../api';
import { applyVolume, sfx } from '../audio';
import { soundRowsHtml } from '../ui/sound';
import { register } from '../router';
import { refreshMe, setSetting, state } from '../state';
import { logout } from '../session';
import { mountLinkButton } from '../ui/google-link';
import { mutedList, unmute } from '../ui/chat';
import { openContact } from '../ui/contact';
import { esc } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { confirmDialog, openModal, toast } from '../ui/overlay';
import { errorText } from '../ui/status';
import { installApp } from '../ui/pwa';
import { disablePush, enablePush, PUSH_KINDS, pushKey, pushState, pushSupport, setPushKinds } from '../ui/push';
import '../ui/fit';

const SECS = [
  ['play', '音・演出'],
  ['notify', '通知'],
  ['chat', 'チャット'],
  ['account', 'アカウント'],
  ['legal', '規約'],
] as const;
let sec: (typeof SECS)[number][0] = 'play';
/** 通知の鍵があるサーバーか（無ければ「通知」タブを出さない） */
let pushReady = false;

register('settings', {
  title: '設定',
  mount(el, params) {
    // ?sec=account などで開くタブを指定（ショップの「Google連携」から）
    const want = SECS.find(([k]) => k === params.get('sec'));
    if (want) sec = want[0];
    const draw = () => {
      const s = state.settings;
      const muted = mutedList();
      el.innerHTML = `<div class="page fill"><div class="panel-paper deco list-page settings" data-sec="${sec}">
        <header class="screen-head"><h1 class="screen-title">${artIcon('gear')}設定</h1></header>
        <div class="tabs seg-tabs set-tabs" role="tablist">${SECS.filter(([k]) => k !== 'notify' || pushReady).map(([k, l]) => `<button class="tab-btn ${k === sec ? 'on' : ''}" role="tab" aria-selected="${k === sec}" data-sec="${k}">${l}</button>`).join('')}</div>
        <div class="set-grid fit-scroll">
        <section class="set-card" data-s="play"><h2>音</h2>
          ${soundRowsHtml()}
        </section>
        <section class="set-card" data-s="play"><h2>演出</h2>
          <div class="seg" role="radiogroup" aria-label="演出速度">${[1, 2, 3].map((v) => `<button role="radio" class="chip ${s.fxSpeed === v ? 'on' : ''}" aria-checked="${s.fxSpeed === v}" data-fx="${v}">${['ふつう', '速い', '最速'][v - 1]}</button>`).join('')}</div>
          <label class="check"><input type="checkbox" data-k="reduceShake" ${s.reduceShake ? 'checked' : ''}> 画面の揺れ・強い発光を減らす</label>
          <div class="set-row"><span>行動の選び方</span><div class="seg" role="radiogroup" aria-label="行動の選び方"><button role="radio" class="chip ${s.palette !== false ? 'on' : ''}" aria-checked="${s.palette !== false}" data-pal="1">盤の上</button><button role="radio" class="chip ${s.palette === false ? 'on' : ''}" aria-checked="${s.palette === false}" data-pal="0">下のボタン</button></div></div>
          <label class="check"><input type="checkbox" data-k="psSkit" ${s.psSkit !== false ? 'checked' : ''}> 節電モードの演出</label>
          <label class="check"><input type="checkbox" data-k="defenseIntro" ${s.skipDefenseIntro ? '' : 'checked'}> 防衛戦の前にルールを出す</label>
          <button class="btn small" data-a="tutorial">${icon('info')}チュートリアルをもう一度</button>
        </section>
        ${pushReady ? '<section class="set-card" data-s="notify"><h2>通知</h2><div class="push-body"></div></section>' : ''}
        <section class="set-card" data-s="chat"><h2>チャット</h2>
          <label class="check"><input type="checkbox" data-k="hideFeats" ${state.profile?.settings?.hideFeats ? 'checked' : ''}> 自分の実績を全体チャットに出さない</label>
          <label class="check"><input type="checkbox" data-k="hideOthersFeats" ${s.hideOthersFeats ? 'checked' : ''}> ほかの人の実績を全体チャットに出さない</label>
          ${muted.length ? `<ul class="muted-list">${muted.map((m) => `<li><span>${esc(m.name ?? m.userId)}<small class="muted">（ブロック）</small></span><button class="btn small" data-unmute="${esc(m.userId)}">解除</button></li>`).join('')}</ul>` : '<p class="muted small">ブロック中の人はいません。</p>'}
        </section>
        <section class="set-card" data-s="account"><h2>アカウント</h2>
          <div class="name-row"><span>表示名</span><b>${esc(state.me?.user.displayName ?? '')}</b>
            <button class="btn small" data-a="rename">名前を変更</button></div>
          <p class="small">お問い合わせ用ID：<code>${esc(state.me?.user.id ?? '')}</code></p>
          ${
            state.me?.user.guest
              ? `<div class="link-google"><p class="small">ゲストで遊んでいます。Googleと連携すると、ほかの端末やブラウザでも同じデータで遊べます。連携しないままログアウトやブラウザのデータ削除をすると、このデータには戻れません。</p>
                  <div class="gsi-slot" aria-label="Googleと連携"></div><p class="gsi-note muted small"></p></div>`
              : '<p class="small">Googleアカウントと連携済みです。</p>'
          }
          <div class="btn-row"><button class="btn" data-a="logout">ログアウト</button><button class="btn danger severe" data-a="delete">アカウント削除</button></div>
        </section>
        <section class="set-card links" data-s="legal"><h2>規約など</h2>
          <a href="/legal/terms.html" target="_blank" rel="noopener">利用規約</a>
          <a href="/legal/tokushoho.html" target="_blank" rel="noopener">特定商取引法に基づく表記</a>
          <a href="/legal/privacy.html" target="_blank" rel="noopener">プライバシーポリシー</a>
          <a href="/legal/community.html" target="_blank" rel="noopener">コミュニティルール</a>
          <button class="link" type="button" data-a="support">お問い合わせ</button>
        </section></div></div></div>`;
    };
    let redraw = () => {
      draw();
      const slot = el.querySelector<HTMLElement>('.link-google .gsi-slot');
      if (!slot) return;
      mountLinkButton(slot, el.querySelector<HTMLElement>('.link-google .gsi-note'), redraw);
    };
    // 通知：この端末の状態はサーバーに聞いてから描く
    const drawPush = async () => {
      const box = el.querySelector<HTMLElement>('.push-body');
      if (!box) return;
      const sup = pushSupport();
      if (sup === 'install') box.innerHTML = `<p class="small">ホーム画面に追加したアプリで受け取れます。</p><button class="btn small" data-a="install">${icon('plus')}ホーム画面に追加</button>`;
      else if (sup === 'unsupported') box.innerHTML = '<p class="muted small">このブラウザでは通知を受け取れません。</p>';
      else if (sup === 'denied') box.innerHTML = '<p class="muted small">通知が端末の設定で止められています。</p>';
      else {
        const kinds = await pushState().catch(() => null);
        const on = kinds != null;
        box.innerHTML = `<label class="check"><input type="checkbox" data-push ${on ? 'checked' : ''}> この端末で受け取る</label>
          <div class="push-kinds">${PUSH_KINDS.map(([k, l]) => `<label class="check"><input type="checkbox" data-pk="${k}" ${!on || kinds.includes(k) ? 'checked' : ''} ${on ? '' : 'disabled'}> ${l}</label>`).join('')}</div>`;
      }
    };
    const redraw0 = redraw;
    redraw = () => {
      redraw0();
      void drawPush();
    };
    redraw();
    void pushKey().then((k) => {
      if (k && !pushReady) {
        pushReady = true;
        redraw();
      }
    });
    const pushKinds = () => [...el.querySelectorAll<HTMLInputElement>('[data-pk]')].filter((x) => x.checked).map((x) => x.dataset.pk!);
    const input = (e: Event) => {
      const t = e.target as HTMLInputElement;
      const k = t.dataset.k;
      if (k === 'bgm' || k === 'sfx' || k === 'master') {
        setSetting(k, Number(t.value) / 100);
        t.nextElementSibling!.textContent = k === 'bgm' ? String(Number(t.value) / 5) : t.value;
        applyVolume();
      } else if (k === 'reduceShake') setSetting('reduceShake', t.checked);
      else if (k === 'psSkit') setSetting('psSkit', t.checked);
      else if (k === 'defenseIntro') setSetting('skipDefenseIntro', !t.checked);
      else if (k === 'hideOthersFeats') setSetting('hideOthersFeats', t.checked);
      else if (k === 'hideFeats') {
        const v = t.checked;
        void api
          .setFeats(v)
          .then(() => {
            if (state.profile) state.profile.settings = { ...state.profile.settings, ...(v ? { hideFeats: true } : {}) };
            if (state.profile && !v) delete state.profile.settings.hideFeats;
            toast(v ? '自分の実績を全体チャットに出しません' : '自分の実績を全体チャットに出します', 'good');
          })
          .catch((err) => {
            t.checked = !v;
            toast(errorText(err), 'error');
          });
      }
    };
    const change = async (e: Event) => {
      const t = e.target as HTMLInputElement;
      if (['sfx', 'master'].includes(t.dataset.k ?? '')) sfx('tap');
      try {
        if (t.matches('[data-push]')) {
          if (!t.checked) await disablePush();
          else if (!(await enablePush(pushKinds()))) toast('通知が許可されませんでした', 'error');
          return void drawPush();
        }
        if (t.matches('[data-pk]')) await setPushKinds(pushKinds());
      } catch (err) {
        toast(errorText(err), 'error');
        void drawPush();
      }
    };
    const click = async (e: Event) => {
      const t = e.target as HTMLElement;
      const sb = t.closest<HTMLElement>('.set-tabs [data-sec]');
      if (sb) {
        sec = sb.dataset.sec as typeof sec;
        return redraw();
      }
      const fx = t.closest<HTMLElement>('[data-fx]');
      if (fx) {
        setSetting('fxSpeed', Number(fx.dataset.fx) as 1 | 2 | 3);
        return redraw();
      }
      const pal = t.closest<HTMLElement>('[data-pal]');
      if (pal) {
        setSetting('palette', pal.dataset.pal === '1');
        return redraw();
      }
      const um = t.closest<HTMLElement>('[data-unmute]');
      if (um) {
        await unmute(um.dataset.unmute!);
        return redraw();
      }
      const a = t.closest<HTMLElement>('[data-a]')?.dataset.a;
      // お問い合わせは「お問い合わせ・要望・報告」の小窓（茶屋のたまきは運営へ取り次がない）
      if (a === 'support') return void openContact('contact');
      if (a === 'tutorial') {
        try {
          await resetTutorial();
          toast('案内を最初から出します（初めての戦い・各画面）', 'good');
        } catch (err) {
          toast(errorText(err), 'error');
        }
        return;
      }
      if (a === 'install') return void installApp();
      if (a === 'rename') {
        renameDialog(redraw);
      } else if (a === 'logout') {
        if (await confirmDialog(state.me?.user.guest ? 'ゲストのままログアウトすると、このデータには二度と戻れません。ログアウトしますか？' : 'ログアウトしますか？', { ok: 'ログアウト' })) await logout();
      } else if (a === 'delete') {
        const ok = await confirmDialog('アカウントを削除すると、育成・購入済みの権利を含むすべてのデータが失われ、元に戻せません。\n本当に削除しますか？', { ok: '削除する', severe: true, title: 'アカウント削除' });
        if (!ok) return;
        const ok2 = await confirmDialog('最終確認です。削除してよろしいですか？', { ok: '完全に削除', severe: true });
        if (!ok2) return;
        try {
          await api.deleteAccount();
          toast('アカウントを削除しました');
          await logout();
        } catch (err) {
          toast(errorText(err), 'error');
        }
      }
    };
    el.addEventListener('input', input);
    el.addEventListener('change', change);
    el.addEventListener('click', click);
    return () => {
      el.removeEventListener('input', input);
      el.removeEventListener('change', change);
      el.removeEventListener('click', click);
    };
  },
});

/** 名前変更（いつでも無料。名前変更券は 2026-10-30 に廃止）。ダイアログごとの nonce で、再送しても1回だけ */
function renameDialog(redraw: () => void) {
  const nonce = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  const m = openModal(
    `<form class="rename-form">
      <label>新しい表示名<input name="name" maxlength="12" minlength="2" required autocomplete="off" value="${esc(state.me?.user.displayName ?? '')}"></label>
      <p class="muted small">2〜12文字。記号の一部・不適切な言葉は使えません。</p>
      <p class="err bad small" hidden></p>
      <div class="btn-row"><button class="btn" type="button" data-close>やめる</button><button class="btn primary">変更する</button></div>
    </form>`,
    { title: '名前を変更' },
  );
  const form = m.body.querySelector<HTMLFormElement>('form');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>('button.primary')!;
    const err = form.querySelector<HTMLElement>('.err')!;
    const name = String(new FormData(form).get('name') ?? '').trim();
    btn.disabled = true;
    err.hidden = true;
    try {
      const r = await api.rename(name, nonce);
      await refreshMe().catch(() => {});
      toast(`表示名を「${r.name}」に変更しました`, 'good');
      m.close();
      redraw();
    } catch (x) {
      err.textContent = errorText(x);
      err.hidden = false;
      btn.disabled = false;
    }
  });
}
